import {
	type AnyFrontendStage,
	type FrontendPlacementMessage,
} from '@post-office/message-maker-browser/types';
import type {
	AnyBackendMessageStage,
	BackendPlacementMessage,
	BackendSyncMessageStage,
} from '@post-office/message-maker-node/placements/types';
import { noValidation } from '@post-office/message-maker-node/placements/message-helpers';
import type {
	AnyPayload,
	CreatePlacementMessageConfig,
} from '@post-office/message-template-contracts/placement-v2';
import {
	PlacementBackend,
	PlacementFrontend,
} from '@post-office/placements--growth/confluence-side-nav';
import type { TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';
import { ConfluenceCloudCcpId } from '@post-office/message-template-contracts/message-template-metadata';

import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';
import { SideNavBanner } from '../../../ui/SideNavBanner';
import { hydrate, type ConfluenceSideNavHydrateResult } from './hydrate';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { PromoModal } from '../../../ui/PromoModal';
import { StorageClient } from '@atlaskit/frontend-utilities/StorageClient';

// Created on first use: constructing a StorageClient on the server defines a global `window`.
let storageClient: StorageClient | undefined;
const getStorageClient = (): StorageClient =>
	(storageClient ??= new StorageClient('confluence-recent-downgrade-discount-modal', {
		storageEngine: 'localStorage',
	}));
const SEEN_KEY = 'seen';

const getSeenValue = (): boolean => getStorageClient().getItem(SEEN_KEY) === true;
const setSeenValue = (): void => getStorageClient().setItemWithExpiry(SEEN_KEY, true);

export const MessageBackend: (config: CreatePlacementMessageConfig) => BackendPlacementMessage<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = PlacementBackend.createMessageBackend(() => ({
	transformMetadata: {
		validate: assertMessageTemplateContext,
		stage: () => ({
			rerankingMetadata: { productId: ConfluenceCloudCcpId },
		}),
	},
	hydrate: {
		validate: assertMessageTemplateContext,
		stage: hydrate,
	},
}));

type MessageFrontendProps = ConfluenceSideNavHydrateResult & {
	handleOnDismissed: () => void;
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: noValidation<MessageFrontendProps>(),
		Component: ({
			productName,
			edition,
			entitlementId,
			placeOrderHref,
			shouldForceOpenModal,
			handleOnDismissed,
		}: MessageFrontendProps) => {
			const [isModalOpen, setIsModalOpen] = useState(() => shouldForceOpenModal && !getSeenValue());

			const analyticsAttributes = useMemo(
				() => ({
					productName,
					edition,
					entitlementId,
				}),
				[productName, edition, entitlementId],
			);

			useEffect(() => {
				if (isModalOpen && !getSeenValue()) {
					setSeenValue();
				}
			}, [isModalOpen]);

			const handleBannerClick = useCallback(() => {
				setIsModalOpen(true);
			}, []);

			const handleModalClose = useCallback(() => {
				setSeenValue();
				setIsModalOpen(false);
			}, []);

			const handleDismissBanner = useCallback(() => {
				setSeenValue();
				handleOnDismissed();
			}, [handleOnDismissed]);

			return (
				<>
					<SideNavBanner
						onClick={handleBannerClick}
						onDismiss={handleDismissBanner}
						productName={productName}
						edition={edition}
						bannerName="confluenceRecentDowngradeDiscountBanner"
						analyticsAttributes={analyticsAttributes}
					/>
					<PromoModal
						isOpen={isModalOpen}
						onClose={handleModalClose}
						productName={productName}
						edition={edition}
						placeOrderHref={placeOrderHref}
						modalName={'confluenceRecentDowngradeDiscountModal'}
					/>
				</>
			);
		},
	},
});
